import './EmailEntryPrintPage.scss';

import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';

import Image from '@/src/components/common/Elements/Image/Image';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import NotAvailable from '@/src/components/notAvailable';
import { useAsync } from '@/src/hooks/useAsync';
import { getDetails } from '@/src/reducers/project/project/project.service';

import EmailEntry from './EmailEntry/EmailEntry';

import { useMappedEmailEntry } from '../../components/EntryPanel/EmailThreadEntryViewer/components/EmailThreadViewer/EmailEntries/EmailEntry/useMappedEmailEntry';

const EmailEntryPrintPage = ({ user }) => {
  const { projectId, emailId, inboxEntryId } = useParams<{
    projectId: string;
    inboxEntryId: string;
    emailId: string;
  }>();

  const handleGetProjectDetails = useCallback(async () => {
    const response = await getDetails(projectId);

    return response;
  }, [projectId]);

  const { isLoading: isLoadingProjectDetails, value: project } = useAsync(handleGetProjectDetails);
  const { emailEntry, isLoading: isLoadingEmailEntry } = useMappedEmailEntry({
    projectId,
    emailId: Number(emailId),
    inboxEntryId: Number(inboxEntryId),
  });
  const [emailEntriesLoaded, setEmailEntriesLoaded] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const isLoading = isLoadingProjectDetails || isLoadingEmailEntry;

  const handleIncrementEmailEntryLoaded = useCallback(() => {
    setEmailEntriesLoaded((prevState) => prevState + 1);
  }, []);

  useEffect(() => {
    if (!emailEntry) {
      return;
    }

    document.title = `${document.title} Mail - ${emailEntry.subject}`;
  }, [emailEntry]);

  useEffect(() => {
    if (!imageLoaded) {
      return;
    }

    // Print is ready
    if (emailEntriesLoaded === 1) {
      window.print();
    }
  }, [emailEntriesLoaded, imageLoaded]);

  if (!projectId || !emailId) {
    return <NotAvailable />;
  }

  if (isLoading) {
    return <Spinner />;
  }

  if (!project || !emailEntry) {
    return <NotAvailable />;
  }

  const userLabel = `${user.firstName} ${user.lastName} <${user.emailAddress}>`;

  return (
    <React.Fragment>
      {!imageLoaded && <Spinner />}
      {/* Main Container */}
      <div
        className={clsx(
          'printPreviewContainer',
          'tw-w-full',
          'tw-my-[25px] tw-mx-auto tw-px-[10px]'
        )}
      >
        {/* Header : Account Logo & user email */}
        <table className="printTableContainer">
          <tbody>
            <tr>
              <td width={'150px'}>
                <Image
                  src={`/api/file/project_pictures/${project.picture}`}
                  alt="Account Logo"
                  className="img-responsive"
                  onLoad={() => setImageLoaded(true)}
                  onError={() => setImageLoaded(true)}
                  showLoader={false}
                />
              </td>
              <td style={{ textAlign: 'right' }}>
                <p className="p-text">
                  <strong>{userLabel}</strong>
                </p>
              </td>
            </tr>
          </tbody>
        </table>

        {/* divider */}
        <hr className="line-divider" />

        {/* Email Entry Content */}
        <EmailEntry emailEntry={emailEntry} onRendered={handleIncrementEmailEntryLoaded} />
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  user: state.loggedUser.data,
});

export default connect(mapStateToProps)(EmailEntryPrintPage);
